import moment from "moment-timezone";
import { AirQualityIndex, DailyWeather, DailyWeatherUnit, HourlyAirQualityIndex, HourlyWeather, HourlyWeatherUnit, Weather } from "../types/Weather";
import date from 'date-and-time';

export const getDateOnlyString = (isoDateString: string | undefined): string | undefined => {
  if (!isoDateString) {
    return undefined;
  }
  const dateObj = new Date(isoDateString);
  return date.format(dateObj, 'YYYY-MM-DD');
}

export const getNextDateOnlyString = (isoDateString: string | undefined): string | undefined => {
  if (!isoDateString) {
    return undefined;
  }
  const dateObj = new Date(isoDateString);
  const nextDay = date.addDays(dateObj, 1);
  return date.format(nextDay, 'YYYY-MM-DD');
}

export const getDateAndDayOfWeek = (dateOnlyString: string): string => {
  const dateObj = date.parse(dateOnlyString, 'YYYY-MM-DD');
  return date.format(dateObj, "dddd, MMM D");
}

export const getHourlyList = (weather: Weather | null): Map<string, HourlyWeather[]> => {
  if (!weather) {
    return new Map<string, HourlyWeather[]>();
  }

  const currentTime = weather.current_weather.time;
  const hourlyWeatherMap = new Map<string, HourlyWeather[]>();
  weather.hourly.time
    .forEach((eachTime, i) => {
      if (eachTime >= currentTime) {
        const dateOnlyStr = getDateOnlyString(eachTime);
        if (dateOnlyStr) {
          let hourlyWeatherList = hourlyWeatherMap.get(dateOnlyStr);
          if (!hourlyWeatherList) {
            hourlyWeatherList = [];
            hourlyWeatherMap.set(dateOnlyStr, hourlyWeatherList);
          }
          hourlyWeatherList.push({
            time: eachTime,
            temperature_2m: weather.hourly.temperature_2m_gfs_global[i],
            apparent_temperature: weather.hourly.apparent_temperature_gfs_global[i],
            relativehumidity_2m: weather.hourly.relativehumidity_2m_gfs_global[i],
            precipitation_probability: weather.hourly.precipitation_probability_gfs_global[i],
            rain: weather.hourly.rain_gfs_global[i],
            showers: weather.hourly.showers_gfs_global[i],
            snowfall: weather.hourly.snowfall_gfs_global[i],
            weathercode: weather.hourly.weathercode_ecmwf_ifs04[i],
            visibility: weather.hourly.visibility_gfs_global[i],
            is_day: weather.hourly.is_day_gfs_global[i],
          });
        }
      }
    });
  return hourlyWeatherMap;
}

export const getCurrentAirQualityIndex = (airQualityIndex: AirQualityIndex | null): HourlyAirQualityIndex | undefined => {
  if (!airQualityIndex) {
    return undefined;
  }

  const localDate = new Date(new Date().toLocaleString("en-US", { timeZone: airQualityIndex.timezone }));
  const localDateStr = date.format(localDate, "YYYY-MM-DDTHH:00");

  // We want the latest aqi  
  const timeIndex = airQualityIndex.hourly.time.indexOf(localDateStr);
  return {
    time: localDateStr,
    timezone: airQualityIndex.timezone,
    us_aqi: airQualityIndex.hourly.us_aqi[timeIndex]
  };
}

export const getDailyList = (weather: Weather | null): Map<string, DailyWeather> => {
  if (!weather) {
    return new Map<string, DailyWeather>();
  }

  const dailyWeatherMap = new Map<string, DailyWeather>();
  weather.daily.time
    .forEach((eachTime, i) => {
      dailyWeatherMap.set(eachTime, {
        time: eachTime,
        weathercode: weather.daily.weathercode_ecmwf_ifs04[i],
        temperature_2m_max: weather.daily.temperature_2m_max_gfs_global[i],
        temperature_2m_min: weather.daily.temperature_2m_min_gfs_global[i],
        uv_index_max: weather.daily.uv_index_max_gfs_global[i],
        precipitation_probability_mean: weather.daily.precipitation_probability_mean_gfs_global[i],
        sunrise: weather.daily.sunrise[i],
        sunset: weather.daily.sunset[i],
      });
    });
  return dailyWeatherMap;
}

export const getDailyWeatherUnit = (weather: Weather | null): DailyWeatherUnit => {

  const dailyUnitsRaw = weather?.daily_units;
  return {
    temperature_2m_max: dailyUnitsRaw?.temperature_2m_max_gfs_global ?? "",
    temperature_2m_min: dailyUnitsRaw?.temperature_2m_min_gfs_global ?? "",
    uv_index_max: dailyUnitsRaw?.uv_index_max_gfs_global ?? "",
    precipitation_probability_mean: dailyUnitsRaw?.precipitation_probability_mean_gfs_global ?? "",
    sunrise: dailyUnitsRaw?.sunrise ?? "",
    sunset: dailyUnitsRaw?.sunset ?? "",
  };
}

export const getHourlyWeatherUnit = (weather: Weather | null): HourlyWeatherUnit => {

  const hourlyUnitsRaw = weather?.hourly_units;
  return {
    apparent_temperature: hourlyUnitsRaw?.apparent_temperature_gfs_global ?? "",
    is_day: hourlyUnitsRaw?.is_day_gfs_global ?? 0,
    precipitation_probability: hourlyUnitsRaw?.precipitation_probability_gfs_global ?? "",
    rain: hourlyUnitsRaw?.rain_gfs_global ?? "",
    showers: hourlyUnitsRaw?.showers_gfs_global ?? "",
    snowfall: hourlyUnitsRaw?.snowfall_gfs_global ?? "",
    temperature_2m: hourlyUnitsRaw?.temperature_2m_gfs_global ?? "",
    relativehumidity_2m: hourlyUnitsRaw?.relativehumidity_2m_gfs_global ?? "",
    visibility: hourlyUnitsRaw?.visibility_gfs_global ?? "",
  };
}


export const getTimeOnlyString = (isoDateString: string): string | undefined => {
  if (!isoDateString) {
    return undefined;
  }
  const dateObj = new Date(isoDateString);
  return date.format(dateObj, 'hA');
}

export const getPreciseTimeOnlyString = (isoDateString: string): string | undefined => {
  if (!isoDateString) {
    return undefined;
  }
  const dateObj = new Date(isoDateString);
  return date.format(dateObj, 'h:mmA');
}

export const getCurrentFormattedDateTime = (): string => {
  const dateObj = new Date();
  return date.format(dateObj, 'MMM D, hh:mm A');
}

export const convertMetersToKilometers = (meters: number): string => {
  const km = meters / 1000;
  return km.toFixed(0);
}

export const getLocalTimeString = (isoDateString: string, timezone: string): string => {
  const dateMoment = moment(isoDateString);
  const timezoneStr = dateMoment.tz(timezone).format('z');

  const localDate = new Date(new Date(isoDateString).toLocaleString("en-US", { timeZone: timezone }));
  const localDateString = date.format(localDate, 'MMM DD - hh:mm A')
  return `${localDateString} (${timezoneStr})`;
}

export const getDirectionFromDeg = (degree: number): string => {
  const angles = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
  const degreeAngle = Math.floor((degree / 22.5) + 0.5);
  return angles[(degreeAngle % 16)];
}